import { useMemo } from "react"

import { ContentTag } from "scrivito"
import { AppSidebarObjClass } from "scrivito/Objs/AppSidebar/AppSidebarObjClass"
import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import {
  LinkListBehavior,
  LinkListContext,
  LinkListContextType
} from "shared/context/LinkListContext"
import { useAppSidebar } from "shared/lib/app-sidebar/app-sidebar-store"
import { useBreakpoints } from "shared/lib/breakpoints/useBreakpoints"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"
import { getBody } from "shared/lib/getDocument"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useOverlays } from "shared/lib/useOverlays"

import { useAuthenticationHandlers } from "features/authentication/model/useAuthenticationHandlers"
import { useAppSidebarSync } from "widgets/app-sidebar/model/useAppSidebarSync"

import { AppSidebarModel } from "../AppSidebar"

export function useAppSidebarModel(): AppSidebarModel {
  const { persistIsExpanded } = useAppSidebarSync()

  const { isOpen, isExpanded, open, close, toggleOpen } = useAppSidebar()
  const { currentOverlay } = useOverlays()

  const { userProfile: neoletterUserProfile, loading: isLoadingUserInfo } =
    useNeoletterUserProfile()
  const { userInfo } = useAuth()
  const { login, logout } = useAuthenticationHandlers()

  const handleAction = useEvent(() => {
    close()
  })

  const linkListContext = useMemo<LinkListContextType>(
    () => ({
      behavior: LinkListBehavior.LIST,
      onClick: handleAction
    }),
    [handleAction]
  )

  const sidebarObj = AppSidebarObjClass.onSite(getCurrentSiteID()).all().first()

  const handleForceCollapse = useEvent(() => {
    close()
    persistIsExpanded(false)
  })

  const handleForceToggleExpanded = useEvent(() => {
    if (isExpanded) {
      handleForceCollapse()
    } else {
      persistIsExpanded(true)
    }
  })

  const handleBreakpointChange = useEvent((bps: Record<string, boolean>) => {
    if (!bps.isMobile) {
      close()
    }
  })

  useBreakpoints(getBody(), {
    breakpoints: {
      isMobile: { maxWidth: 80 }
    },
    onChange: handleBreakpointChange
  })

  return {
    onOpen: open,
    onClose: close,

    onLogin: login,
    onLogout: logout,

    isOpen,
    isExpanded,

    onToggleOpen: toggleOpen,
    onToggleExpanded: handleForceToggleExpanded,

    userInfo: neoletterUserProfile || userInfo,
    isLoadingUserInfo,
    inert: !!currentOverlay,
    userDetailsSlot: (
      <ContentTag content={sidebarObj} attribute="userDetailsSlot" />
    ),
    menuLinks: (
      <LinkListContext.Provider value={linkListContext}>
        <ContentTag content={sidebarObj} attribute="menuLinks" />
      </LinkListContext.Provider>
    ),
    menuLinksSecondary: (
      <LinkListContext.Provider value={linkListContext}>
        <ContentTag content={sidebarObj} attribute="menuLinksSecondary" />
      </LinkListContext.Provider>
    ),
    menuLinksTertiary: (
      <LinkListContext.Provider value={linkListContext}>
        <ContentTag content={sidebarObj} attribute="menuLinksTertiary" />
      </LinkListContext.Provider>
    )
  }
}
